import React, { useContext, useEffect, useState } from 'react'
import { CmsDataFetchStatus } from '@jarvis/olex-content-management'
import useContentStack from '../hooks/useContentStack'
import classes from './ScanningFeaturesNoticeModal.module.scss'
import {
  ContentStackTypes,
  VENEER_PRIMARY_APPEARANCE,
  VENEER_SECONDARY_APPEARANCE
} from '../store/Constants'
import { ConfigContext } from '../store/ConfigContext'
import useEasyStartDownload from '../hooks/useEasyStartDownload'
import renderButtonsInHtml from '../utils/renderButtonsInHtml'
import Modal from '@veneer/core/dist/esm/scripts/modal'
import { ANALYTICS, useAnalyticsAPI } from '../analytics'

export const ScanningFeaturesNoticeModal = ({
  open,
  onClose,
  parentScreen
}) => {
  const { cachedCMS, flowActions, currentPath } = useContext(ConfigContext)

  const inferredParentScreenByPageId = {
    'get-software': ANALYTICS.SCREENS.INSTALL_HP_EASY_START_GENERIC,
    'easy-start':
      currentPath === '/setup'
        ? ANALYTICS.SCREENS.INSTALL_HP_EASY_START_GENERIC
        : ANALYTICS.SCREENS.INSTALL_HP_EASY_START
  }[flowActions?.currentFlow?.page_id]

  const resolvedParentScreen = parentScreen || inferredParentScreenByPageId

  const modalScreen = {
    ...ANALYTICS.MODALS.scanning_features_notice,
    screenPath: resolvedParentScreen
      ? `${resolvedParentScreen.screenPath}${resolvedParentScreen.screenName}/`
      : ANALYTICS.MODALS.scanning_features_notice.screenPath
  }

  const { trackScreenDisplay, trackButtonClick } = useAnalyticsAPI(
    modalScreen,
    true
  )

  useEffect(() => {
    if (open) {
      trackScreenDisplay({ force: true })
    }
  }, [open])

  const parseContentStackModalData = (data) => {
    return {
      header: data?.header,
      content: data?.content[0]?.copy?.copy || ''
    }
  }

  const { status, pageData } = useContentStack(
    ContentStackTypes.modal_scanning_features_notice,
    parseContentStackModalData
  )

  const tdeLayoutCache = cachedCMS.get(
    ContentStackTypes.tde_layout.content_type_uid
  )

  // Arrange Install HP Easy Start button
  const [loadingDriver, setLoadingDriver] = useState(false)
  const installHPEasyStart = tdeLayoutCache.install_hp_easy_start
  // Integrate unified Easy Start download logic with SKU-aware fallback to OS
  const { installEasyStart } = useEasyStartDownload({
    setLoading: setLoadingDriver
  })
  const handleEasyStartButtonClick = () => {
    trackButtonClick(ANALYTICS.BUTTONS.INSTALL_HP_EASY_START)
    // Simply invoke hook method; handles SKU present vs OS fallback internally
    installEasyStart()
  }

  // Arrange Visit HP Support button
  const visitHpSupport = tdeLayoutCache.visit_hp_support
  const handleVisitHpSupportButtonClick = () => {
    trackButtonClick(ANALYTICS.BUTTONS.VISIT_HP_SUPPORT)
    window.open('https://support.hp.com/printer-setup', '_blank', 'noopener')
  }

  /**
   * Array of buttons to parse and render in the modal content.
   * Each object contains a button HTML string and props for the Button component.
   * @type {[{button: string, props: ButtonProps}]}
   * Type definition for button props used in the modal.
   * @typedef {Object} ButtonProps
   * @property {() => void} onClick - Click handler for the button.
   * @property {string} appearance - Button appearance style.
   * @property {boolean} [loading] - Loading state for the button.
   * @property {string} ['data-testid'] - Test id for the button.
   * @property {string} buttonName - Display name for the button.
   */
  const buttonsToParse = [
    {
      button: '<button>[[ InstallHpEasyStart ]]</button>',
      props: {
        onClick: () => handleEasyStartButtonClick(),
        appearance: VENEER_PRIMARY_APPEARANCE,
        loading: loadingDriver,
        'data-testid': 'simple-landing-page-modal-hp-easy-start',
        buttonName: installHPEasyStart
      }
    },
    {
      button: '<button>[[ VisitHpSupport ]]</button>',
      props: {
        onClick: () => handleVisitHpSupportButtonClick(),
        appearance: VENEER_SECONDARY_APPEARANCE,
        'data-testid': 'simple-landing-page-modal-hp-support',
        buttonName: visitHpSupport
      }
    }
  ]

  const contentRendered = renderButtonsInHtml({
    htmlString: pageData?.content,
    buttonsToParse
  })

  if (status === CmsDataFetchStatus.LOADING) {
    return <></>
  }

  return (
    <Modal
      className={classes.scanningFeaturesNoticeModalContainer}
      show={open}
      closeButton
      expanded={true}
      maxWidth="1000px"
      id="scanning-features-notice-modal"
      title={pageData?.header}
      onClose={() => {
        trackButtonClick(ANALYTICS.BUTTONS.CLOSE_MODAL)
        onClose()
      }}
      portal
    >
      {contentRendered}
    </Modal>
  )
}
